import React, { useEffect, useMemo } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { GeneralInput, GeneralButton } from "../../../components";

interface ConfirmPasswordFormInputs {
  Password: string;
}

interface ConfirmPasswordFormProps {
  onSubmitSuccess: (data: ConfirmPasswordFormInputs) => void;
  onCancel: () => void;
  isPending?: boolean;
}

const ConfirmPasswordForm: React.FC<ConfirmPasswordFormProps> = ({
  onSubmitSuccess,
  onCancel,
  isPending = false,
}) => {
  const { t } = useTranslation();

  const confirmPasswordSchema = useMemo(() => {
    return z.object({
      Password: z
        .string()
        .nonempty({ message: t("required_field") })
        .min(8, { message: t("at_least_8_characters") }),
    });
  }, [t]);

  const methods = useForm<ConfirmPasswordFormInputs>({
    resolver: zodResolver(confirmPasswordSchema),
    defaultValues: {
      Password: "",
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    trigger,
    formState: { isSubmitted },
  } = methods;

  useEffect(() => {
    if (isSubmitted) {
      trigger();
    }
  }, [t, trigger, isSubmitted]);

  const onSubmit: SubmitHandler<ConfirmPasswordFormInputs> = (data) => {
    onSubmitSuccess(data);
  };

  return (
    <FormProvider {...methods}>
      <h2 id="confirm-password-title" className="text-body-text mb-2">
        {t("please_enter_your_password_to_confirm_it_is_you")}
      </h2>

      <form
        className="w-full"
        onSubmit={handleSubmit(onSubmit)}
        aria-labelledby="confirm-password-title"
      >
        <GeneralInput
          type="password"
          name="Password"
          label={t("password")}
          placeholder={t("please_enter_your_password")}
          required
        />
        <div className="mt-10 flex items-center justify-end gap-3">
          <GeneralButton
            title={t("cancel")}
            color="liteGray"
            onClick={onCancel}
          />
          <GeneralButton
            type="submit"
            disabled={isPending}
            className="!px-5"
            title={t("next")}
          />
        </div>
      </form>
    </FormProvider>
  );
};

export default ConfirmPasswordForm;
